import './App.css';
import '@aws-amplify/ui-react/styles.css';
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { Authenticator, ColorMode, ThemeProvider, defaultDarkModeOverride } from '@aws-amplify/ui-react';
import amplifyService from './services/amplify.service.ts';
import { Button, Modal, Form, Spinner, ListGroup } from 'react-bootstrap';
import { Chessboard } from 'react-chessboard';
import { Chess, Square } from 'chess.js';
import { FaSearch, FaPuzzlePiece, FaSave, FaStepBackward, FaStepForward, FaTimes, FaCheck, FaArrowRight, FaExternalLinkAlt, FaHeart, FaRegHeart, FaUsers } from 'react-icons/fa';
import { HiSwitchHorizontal } from 'react-icons/hi';
import AppHeader from './components/AppHeader.tsx';
import Lobby from './pages/Lobby.tsx';
import Profile from './pages/Profile.tsx';
import Leaderboard from './pages/Leaderboard.tsx';
import Feedback from './pages/Feedback.tsx';

interface PuzzleData {
  Id: string,
  Fen: string;
  WhitePlayer: string;
  BlackPlayer: string;
  Date: string;
  MoveIndex: number;
  Turn: boolean;
  Scores: { [key: string]: number };
}

function App() {
  amplifyService.initialize();
  const [colorMode, setColorMode] = React.useState<ColorMode>('dark');
  const theme = {
    name: 'my-theme',
    overrides: [defaultDarkModeOverride],
  };



  // User bilgilerini işleyecek fonksiyon
  const authInitializedRef = React.useRef(false);
  const handleUserAuthentication = async (user: any) => {
    // Her render'da çağrılmasını engelle
    if (authInitializedRef.current) return user;
    authInitializedRef.current = true;

    const userData: any = {
      id: user.username,
      score: 0,
      count: 0,
      time: 0,
      favoritePuzzles: [] as any[],
      puzzleScores: {} as { [key: string]: number },
      puzzleTimes: {} as { [key: string]: number },
      puzzleSolvedAt: {} as { [key: string]: number },
      solvedPuzzles: [] as string[]
    };

    try {
      const existingUser = await amplifyService.getUser(userData.id);
      if (!existingUser) {
        await amplifyService.createUser({ id: userData.id, score: userData.score, count: userData.count, time: userData.time });
        localStorage.setItem('UserData', JSON.stringify(userData));
        localStorage.setItem(`UserData_${user.username}`, JSON.stringify(userData));
        console.log('New user successfully created in database');
      } else {
        userData.id = existingUser.id;
        userData.score = existingUser.score || 0;
        userData.count = existingUser.count || 0;
        userData.time = existingUser.time || 0;
        userData.favoritePuzzles = existingUser.favoritePuzzles || [];
        userData.solvedPuzzles = existingUser.solvedPuzzles || [];

        // 🔒 puzzleScores, puzzleTimes, puzzleSolvedAt'ı DB'den al
        try {
          if (existingUser.puzzleScores) {
            userData.puzzleScores = typeof existingUser.puzzleScores === 'string'
              ? JSON.parse(existingUser.puzzleScores)
              : existingUser.puzzleScores;
          }
        } catch (e) { console.error('Error parsing puzzleScores in auth:', e); }
        try {
          if ((existingUser as any).puzzleTimes) {
            userData.puzzleTimes = typeof (existingUser as any).puzzleTimes === 'string'
              ? JSON.parse((existingUser as any).puzzleTimes)
              : (existingUser as any).puzzleTimes;
          }
        } catch (e) { console.error('Error parsing puzzleTimes in auth:', e); }
        try {
          if ((existingUser as any).puzzleSolvedAt) {
            userData.puzzleSolvedAt = typeof (existingUser as any).puzzleSolvedAt === 'string'
              ? JSON.parse((existingUser as any).puzzleSolvedAt)
              : (existingUser as any).puzzleSolvedAt;
          }
        } catch (e) { console.error('Error parsing puzzleSolvedAt in auth:', e); }

        // ✅ Her iki key'e de TAM veri yaz
        localStorage.setItem('UserData', JSON.stringify(userData));
        localStorage.setItem(`UserData_${user.username}`, JSON.stringify(userData));
        console.log('User already exists in database, loaded with full puzzle data');
      }
    } catch (error) {
      console.error('Error checking/creating user in database:', error);
    }

    return user;
  };

  const authComponents = {
    Header() {
      return (
        <div style={{ padding: 'clamp(1.5rem, 5vh, 3.5rem) 1rem clamp(1rem, 3vh, 2rem) 1rem', textAlign: 'center' }}>
          <h2 className="auth-custom-title">
            Boun Chess
          </h2>
          <p className="auth-custom-subtitle">
            Master Your Chess Vision
          </p>
        </div>
      );
    },
  };

  return (
    <ThemeProvider>
      <Authenticator hideSignUp components={authComponents}>
        {({ signOut, user }) => {
          handleUserAuthentication(user);

          const handleSignOut = () => {
            // 🔒 Sadece kullanıcıya özel key'leri sil, tüm localStorage'ı temizleme
            const username = user?.username;
            if (username) {
              localStorage.removeItem('UserData');
              localStorage.removeItem(`UserData_${username}`);
            } else {
              localStorage.clear();
            }
            authInitializedRef.current = false;
            if (signOut) signOut();
          };

          return (
            <Router>
              <AppHeader
                onSignOut={handleSignOut}
              />
              <Routes>
                <Route
                  path="/"
                  element={
                    <div className="Board">
                      <Lobby user={user} />
                    </div>
                  }
                />
                <Route path="/board" element={<Board signOut={handleSignOut} user={user} />} />
                <Route
                  path="/leaderboard"
                  element={
                    <div className="Board">
                      <Leaderboard />
                    </div>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <div className="Board">
                      <Profile user={user} />
                    </div>
                  }
                />
                <Route
                  path="/profile/:username"
                  element={
                    <div className="Board">
                      <Profile user={user} />
                    </div>
                  }
                />
                <Route
                  path="/feedback"
                  element={
                    <div className="Board">
                      <Feedback user={user} />
                    </div>
                  }
                />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </Router>
          );
        }}
      </Authenticator>
    </ThemeProvider>
  );
}

function Board({ signOut, user }: { signOut: any; user: any }) {
  const navigate = useNavigate();
  const [position, setPosition] = useState('start');
  const [history, setHistory] = useState<any[]>([]);
  const [currentMove, setCurrentMove] = useState(0);
  const [highlightedSquares, setHighlightedSquares] = useState({});
  const chess = useRef(new Chess());
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [orientation, setOrientation] = useState<'white' | 'black'>('white');
  const timerIntervalRef = useRef<number | null>(null);
  const getOrientationFromFen = (fen: string): 'white' | 'black' => {
    try {
      const parts = fen.split(' ');
      const side = parts[1];
      return side === 'b' ? 'black' : 'white';
    } catch (_e) {
      return 'white';
    }
  };
  const [searchInput, setSearchInput] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [whitePlayerName, setWhitePlayerName] = useState('');
  const [blackPlayerName, setBlackPlayerName] = useState('');
  const [games, setGames] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingPuzzles, setLoadingPuzzles] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [timer, setTimer] = useState<{ time: number; intervalId: number | null }>({ time: 0, intervalId: null });
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveInfo, setMoveInfo] = useState<{
    san: string;
    score: number | undefined;
    time: string;
    scoreChange: number;
    previousTotalScore?: number;
    bestMove?: string;
    bestScore?: number;
    topMoves?: Array<{ move: string; score: number; rank: number; raw: string }>;
    userRank?: number;
    userMoveKey?: string;
    userMoveSan?: string;
  } | null>(null);
  const [currentPuzzleData, setCurrentPuzzleData] = useState<PuzzleData | null>(null);
  const [puzzleScored, setPuzzleScored] = useState<boolean>(false);
  const [initialPuzzleFen, setInitialPuzzleFen] = useState<string | null>(null);
  const [userScore, setUserScore] = useState(0);
  const [puzzleStartScore, setPuzzleStartScore] = useState<number | null>(null);
  const [firstMoveScore, setFirstMoveScore] = useState<{ score: number; scoreChange: number; time: string } | null>(null);
  const [bestMoveArrow, setBestMoveArrow] = useState<Array<[Square, Square, string?]>>([]);
  const [isLoadingNext, setIsLoadingNext] = useState<boolean>(false);
  const [nextButtonDisabled, setNextButtonDisabled] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
  const [activeTab, setActiveTab] = useState<'controls' | 'history'>('controls');
  const [favoritePuzzles, setFavoritePuzzles] = useState<string[]>([]);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [puzzleMoveStats, setPuzzleMoveStats] = useState<{ [key: string]: number } | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [allPuzzles, setAllPuzzles] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [showDateSelectionModal, setShowDateSelectionModal] = useState(false);
  const [availableDates, setAvailableDates] = useState<string[]>([]);

  const formatMoveScore = useCallback((value?: number | null) => {
    if (value === undefined || value === null || Number.isNaN(value)) {
      return '-';
    }
    const percentValue = value * 100;
    return percentValue.toLocaleString('tr-TR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
  }, []);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // User score'unu localStorage'dan al veya yeni oluştur
  const [userTotalScore, setUserTotalScore] = useState(() => {
    const ud = localStorage.getItem(`UserData_${user.username}`) || localStorage.getItem('UserData');
    if (ud) {
      const parsed = JSON.parse(ud);
      return parsed.totalScore || 0;
    }
    return 0;
  });

  // Score güncellendiğinde localStorage'ı da güncelle
  useEffect(() => {
    const udSync = localStorage.getItem(`UserData_${user.username}`) || localStorage.getItem('UserData');
    if (udSync) {
      const parsed = JSON.parse(udSync);
      parsed.score = userTotalScore;
      localStorage.setItem(`UserData_${user.username}`, JSON.stringify(parsed));
      localStorage.setItem('UserData', JSON.stringify(parsed));
    }
  }, [userTotalScore]);

  // Puzzle çözüldüğünde score'u güncelle
  const updateUserScore = async (newScore: number) => {
    const updatedTotalScore = userTotalScore + newScore;
    setUserTotalScore(updatedTotalScore);

    // localStorage güncelle
    const udUpdate = localStorage.getItem(`UserData_${user.username}`) || localStorage.getItem('UserData');
    if (udUpdate) {
      const parsed = JSON.parse(udUpdate);
      parsed.score = updatedTotalScore;
      localStorage.setItem(`UserData_${user.username}`, JSON.stringify(parsed));
      localStorage.setItem('UserData', JSON.stringify(parsed));
    }

    // Veritabanını güncelle
    try {
      //await amplifyService.updateUserScore(user.username, updatedTotalScore);
      console.log('User score successfully updated in database');
    } catch (error) {
      console.error('Error updating user score in database:', error);
    }
  };

  React.useEffect(() => {
    window.scrollTo(0, 0);
    // capture initial base FEN for history navigation when not in a puzzle
    try {
      setInitialPuzzleFen(chess.current.fen());
    } catch (_e) { }

    // Component unmount olduğunda timer'ı temizle
    return () => {
      if (timerIntervalRef.current !== null) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, []);

  React.useEffect(() => {
    setGames([]);
    if (searchPerformed) {
      setSearchPerformed(false);
    }
  }, [searchInput]);

  const handleSignOut = async () => {
    signOut();
  };


  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const toggleFavorite = async () => {
    if (!currentPuzzleData || !user) return;

    const puzzleId = currentPuzzleData.Id;
    const isFavorite = favoritePuzzles.includes(puzzleId);

    let newFavorites;
    if (isFavorite) {
      newFavorites = favoritePuzzles.filter(id => id !== puzzleId);
    } else {
      newFavorites = [...favoritePuzzles, puzzleId];
    }

    setFavoritePuzzles(newFavorites);

    // Update DB
    try {
      await amplifyService.updateUser({
        id: user.username,
        favoritePuzzles: newFavorites
      });

      // Update local storage
      if (userData) {
        const updatedUserData = { ...userData, favoritePuzzles: newFavorites };
        setUserData(updatedUserData);
        localStorage.setItem(`UserData_${user.username}`, JSON.stringify(updatedUserData));
      }
    } catch (error) {
      console.error("Error updating favorites:", error);
      // Revert state on error
      setFavoritePuzzles(favoritePuzzles);
    }
  };

  const handleShowStats = async () => {
    if (!currentPuzzleData) return;

    setLoadingStats(true);
    setShowStatsModal(true);

    try {
      const puzzle = await amplifyService.getPuzzle(currentPuzzleData.Id);
      if (puzzle && puzzle.moveStats) {
        if (typeof puzzle.moveStats === 'string') {
          setPuzzleMoveStats(JSON.parse(puzzle.moveStats));
        } else {
          setPuzzleMoveStats(puzzle.moveStats as { [key: string]: number });
        }
      } else {
        setPuzzleMoveStats({});
      }
    } catch (error) {
      console.error("Error loading stats:", error);
      setPuzzleMoveStats({});
    } finally {
      setLoadingStats(false);
    }
  };

  const handleBackMove = () => {
    if (currentMove > 0) {
      const newMoveIndex = currentMove - 1;
      goToMoveIndex(newMoveIndex);
    }
  };

  const handleForwardMove = () => {
    if (currentMove < history.length) {
      const newMoveIndex = currentMove + 1;
      goToMoveIndex(newMoveIndex);
    }
  };

  // Keyboard navigation: Left = Back, Right = Forward
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      // Ignore when typing in inputs/textareas
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || (e.target as HTMLElement)?.isContentEditable) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handleBackMove();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleForwardMove();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [currentMove, history.length]);


  const handleSearchClick = async () => {
    setLoadingSearch(true);
    const games = await amplifyService.getGame(searchInput);
    setGames(games);
    setLoadingSearch(false);
    setSearchPerformed(true);
  };

  const handleSaveGame = () => {
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  const handleModalSave = () => {
    amplifyService.createGame({ white: whitePlayerName, black: blackPlayerName, moves: chess.current.fen() })
    setShowModal(false);
  };

  const handleGameClick = (game: any) => {
    setPosition(game.moves);
    chess.current.load(game.moves);
    setHistory([]);
    setCurrentMove(0);
    setHighlightedSquares({});
    setInitialPuzzleFen(game.moves);
  };


  const [solvedPuzzles, setSolvedPuzzles] = useState<string[]>([]);

  const [alertMessage, setAlertMessage] = useState('');
  const [showAlertModal, setShowAlertModal] = useState(false);

  const [userData, setUserData] = useState<any>(null);

  const [puzzleCache, setPuzzleCache] = useState<any[]>([]);


  useEffect(() => {
    const loadUserData = async () => {
      try {
        const cachedData = localStorage.getItem(`UserData_${user.username}`);
        let cachedPuzzleScores: { [key: string]: number } = {};
        let cachedPuzzleTimes: { [key: string]: number } = {};
        let cachedPuzzleSolvedAt: { [key: string]: number } = {};

        if (cachedData) {
          const parsed = JSON.parse(cachedData);
          setUserData(parsed);
          cachedPuzzleScores = parsed.puzzleScores || {};
          cachedPuzzleTimes = parsed.puzzleTimes || {};
          cachedPuzzleSolvedAt = parsed.puzzleSolvedAt || {};

          if (parsed.solvedPuzzles) {
            const validPuzzles = parsed.solvedPuzzles.filter((p: any) => p !== null) as string[];
            setSolvedPuzzles(validPuzzles);
          }
          if (parsed.favoritePuzzles) {
            const validFavorites = parsed.favoritePuzzles.filter((p: any) => p !== null) as string[];
            setFavoritePuzzles(validFavorites);
          }
        }

        // 🔄 SYNC: DB'den fresh data çek ve localStorage ile merge et
        const freshData = await amplifyService.getUser(user.username);
        if (freshData) {
          // DB'deki puzzleScores'ı parse et
          let dbPuzzleScores: { [key: string]: number } = {};
          try {
            if (freshData.puzzleScores) {
              if (typeof freshData.puzzleScores === 'string') {
                dbPuzzleScores = JSON.parse(freshData.puzzleScores);
              } else if (typeof freshData.puzzleScores === 'object') {
                dbPuzzleScores = freshData.puzzleScores as { [key: string]: number };
              }
            }
          } catch (e) {
            console.error('Error parsing DB puzzleScores:', e);
          }

          // DB'deki puzzleTimes'ı parse et
          let dbPuzzleTimes: { [key: string]: number } = {};
          try {
            if ((freshData as any).puzzleTimes) {
              if (typeof (freshData as any).puzzleTimes === 'string') {
                dbPuzzleTimes = JSON.parse((freshData as any).puzzleTimes);
              } else if (typeof (freshData as any).puzzleTimes === 'object') {
                dbPuzzleTimes = (freshData as any).puzzleTimes as { [key: string]: number };
              }
            }
          } catch (e) {
            console.error('Error parsing DB puzzleTimes:', e);
          }

          // DB'deki puzzleSolvedAt'ı parse et
          let dbPuzzleSolvedAt: { [key: string]: number } = {};
          try {
            if ((freshData as any).puzzleSolvedAt) {
              if (typeof (freshData as any).puzzleSolvedAt === 'string') {
                dbPuzzleSolvedAt = JSON.parse((freshData as any).puzzleSolvedAt);
              } else if (typeof (freshData as any).puzzleSolvedAt === 'object') {
                dbPuzzleSolvedAt = (freshData as any).puzzleSolvedAt as { [key: string]: number };
              }
            }
          } catch (e) {
            console.error('Error parsing DB puzzleSolvedAt:', e);
          }

          // 🚨 CONFLICT DETECTION: LocalStorage boşsa, DB'yi kullan
          const localScoreCount = Object.keys(cachedPuzzleScores).length;
          const dbScoreCount = Object.keys(dbPuzzleScores).length;

          let mergedPuzzleScores = { ...dbPuzzleScores, ...cachedPuzzleScores };
          let mergedPuzzleTimes = { ...dbPuzzleTimes, ...cachedPuzzleTimes };
          let mergedPuzzleSolvedAt = { ...dbPuzzleSolvedAt, ...cachedPuzzleSolvedAt };

          if (localScoreCount === 0 && dbScoreCount > 0) {
            console.log(`✅ SYNC: LocalStorage was empty. Restored ${dbScoreCount} scores from database.`);
            mergedPuzzleScores = dbPuzzleScores;
            mergedPuzzleTimes = dbPuzzleTimes;
            mergedPuzzleSolvedAt = dbPuzzleSolvedAt;
          } else if (dbScoreCount > localScoreCount) {
            console.log(`🔄 MERGE: DB has ${dbScoreCount} scores, Local has ${localScoreCount}. Merging both.`);
          }

          const updatedData = {
            ...freshData,
            solvedPuzzles: freshData.solvedPuzzles || [],
            favoritePuzzles: freshData.favoritePuzzles || [],
            puzzleScores: mergedPuzzleScores,
            puzzleTimes: mergedPuzzleTimes,
            puzzleSolvedAt: mergedPuzzleSolvedAt
          };

          setUserData(updatedData);
          localStorage.setItem(`UserData_${user.username}`, JSON.stringify(updatedData));

          if (updatedData.solvedPuzzles) {
            const validPuzzles = updatedData.solvedPuzzles.filter(p => p !== null) as string[];
            setSolvedPuzzles(validPuzzles);
          }
          if (updatedData.favoritePuzzles) {
            const validFavorites = updatedData.favoritePuzzles.filter(p => p !== null) as string[];
            setFavoritePuzzles(validFavorites);
          }
        }
      } catch (error) {
        console.error("Error loading user data:", error);
      }
    };

    if (user) {
      loadUserData();
    }
  }, [user]);

  const loadPuzzleBatch = useCallback(async () => {
    if (allPuzzles.length > 0) return;

    try {
      // DB'den live solved puzzles çek
      let dbSolvedPuzzles: string[] = [];
      if (user) {
        try {
          const dbUser = await amplifyService.getUser(user.username);
          if (dbUser && dbUser.solvedPuzzles) {
            dbSolvedPuzzles = dbUser.solvedPuzzles.filter((p: any) => p !== null) as string[];
          }
        } catch (error) {
          console.error("Error fetching solved puzzles from DB:", error);
        }
      }

      // Tüm puzzle'ları çek
      const puzzles = await amplifyService.getPuzzleBatch();

      // DB'deki solved puzzles'a göre filtrele
      const unsolvedPuzzles = puzzles.filter(p => {
        const fen = (p as any).fen;
        return !dbSolvedPuzzles.includes(p.id) && (!fen || !dbSolvedPuzzles.includes(fen));
      });

      // Group by date
      const dates = new Set<string>();
      unsolvedPuzzles.forEach(p => {
        if ((p as any).createdAt) {
          const date = new Date((p as any).createdAt).toLocaleDateString('tr-TR');
          dates.add(date);
        }
      });

      const sortedDates = Array.from(dates).sort((a, b) => {
        const [d1, m1, y1] = a.split('.').map(Number);
        const [d2, m2, y2] = b.split('.').map(Number);
        return new Date(y2, m2 - 1, d2).getTime() - new Date(y1, m1 - 1, d1).getTime();
      });

      setAllPuzzles(unsolvedPuzzles);
      setAvailableDates(sortedDates);

      if (sortedDates.length > 0) {
        setShowDateSelectionModal(true);
      } else {
        // No dates found? Maybe just set cache directly if any puzzles exist without date?
        // Or just set cache with whatever we have
        setPuzzleCache(unsolvedPuzzles);
      }

    } catch (error) {
      console.error("Error loading puzzle batch:", error);
    }
  }, [user, allPuzzles.length]);

  useEffect(() => {
    if (user && puzzleCache.length === 0 && selectedDate && allPuzzles.length > 0) {
      // If we finished puzzles for the selected date, show modal again
      setSelectedDate(null);
      setShowDateSelectionModal(true);
    }
  }, [user, puzzleCache.length, allPuzzles.length, selectedDate]);




  // Sadece LOCAL state/localStorage'ı günceller (anlık yansıma için).
  // DB yazımı artık recordSolvedPuzzle ile ATOMİK yapılıyor; burada ayrı DB
  // çağrısı YOK. localStorage her zaman TAZE okunur — bayat `userData` state'i
  // kaynak alınmaz (eski "puanları ezme" hatasının kökü buydu).
  const addSolvedPuzzleLocally = useCallback((puzzleId: string) => {
    setSolvedPuzzles(prev => Array.from(new Set([...prev, puzzleId])));

    try {
      const key = `UserData_${user.username}`;
      const raw = localStorage.getItem(key) || localStorage.getItem('UserData');
      const parsed = raw ? JSON.parse(raw) : {};
      const updatedSolvedPuzzles = Array.from(new Set([...(parsed.solvedPuzzles || []), puzzleId]));
      const updatedUserData = { ...parsed, solvedPuzzles: updatedSolvedPuzzles };
      setUserData(updatedUserData);
      localStorage.setItem(key, JSON.stringify(updatedUserData));
      localStorage.setItem('UserData', JSON.stringify(updatedUserData));
    } catch (error) {
      console.error("Error updating solved puzzles locally:", error);
    }
  }, [user]);

  // Puzzle çözme mantığını içeren ortak fonksiyon
  const handlePuzzleMove = useCallback((sourceSquare: Square, targetSquare: Square, move: any, preFen: string) => {
    if (timer.intervalId !== null && currentPuzzleData) {
      // New scoring: error = bestScore - userScore (lower is better)
      const scoresMap: any = currentPuzzleData.Scores || {};
      const userMoveKey: any = (move as any).lan;
      const userMoveScore: number = (scoresMap && scoresMap[userMoveKey] !== undefined) ? scoresMap[userMoveKey] : 0;
      const bestEntry = Object.entries(scoresMap).reduce<{ key: string; val: number }>((acc: any, [k, v]: any) => {
        const vv = Number(v);
        if (acc === null || vv > acc.val) return { key: k as string, val: vv };
        return acc;
      }, { key: '', val: -Infinity });
      const bestScore = bestEntry.val === -Infinity ? 0 : bestEntry.val;
      const bestMoveKey = bestEntry.key;
      const delta = Number((bestScore - (userMoveScore || 0)).toFixed(3));
      const sortedTopRaw = Object.entries(scoresMap)
        .map(([k, v]) => ({ move: k as string, score: Number(v) }))
        .sort((a, b) => b.score - a.score)
        .slice(0, 5);
      // Convert to SAN using a temp board from current position
      const sortedTop = sortedTopRaw.map((entry, idx) => {
        const chessForMoves = new Chess(preFen);
        const legalMoves: any[] = chessForMoves.moves({ verbose: true }) as any[];
        const normalKey = entry.move.replace(/[+#?!]/g, '');
        const keyNoCapture = normalKey.replace('x', '');

        const preferMove = (candidates: any[]): any | null => {
          if (!candidates || candidates.length === 0) return null;
          const pawnCaptures = candidates.filter((m) => m.piece === 'p' && m.from[0] !== m.to[0]);
          if (pawnCaptures.length > 0) return pawnCaptures[0];
          const captures = candidates.filter((m) => (typeof m.flags === 'string' ? m.flags.includes('c') : m.flags));
          if (captures.length > 0) return captures[0];
          return candidates[0];
        };

        const sanMatches = legalMoves.filter((m) => {
          const sanNorm = (m.san as string).replace(/[+#?!]/g, '');
          return sanNorm === normalKey || sanNorm === keyNoCapture;
        });

        let selectedMove = preferMove(sanMatches);

        if (!selectedMove) {
          const lanMatches = legalMoves.filter((m) => {
            if (!m.lan) return false;
            const lanNorm = (m.lan as string).replace(/[+#?!]/g, '');
            return lanNorm === normalKey || lanNorm === keyNoCapture;
          });
          selectedMove = preferMove(lanMatches);
        }

        if (!selectedMove) {
          const coordMatches = legalMoves.filter((m) => {
            const lan = `${m.from}${m.to}${m.promotion ? m.promotion : ''}`;
            return lan === normalKey || lan === keyNoCapture;
          });
          selectedMove = preferMove(coordMatches);
        }

        if (!selectedMove) {
          const destMatches = legalMoves.filter(
            (m) => m.to === normalKey || m.to === keyNoCapture
          );
          selectedMove = preferMove(destMatches);
        }

        if (!selectedMove) {
          try {
            const tmp = new Chess(preFen);
            const sanAttempt: any = tmp.move(entry.move, { sloppy: true } as any);
            if (sanAttempt && sanAttempt.san) {
              selectedMove = sanAttempt;
            }
          } catch (_ignored) { }
        }

        let sanLabel = entry.move;
        let rawLabel = entry.move;

        if (selectedMove) {
          sanLabel = (selectedMove.san || sanLabel) as string;
          if (
            selectedMove.piece === 'p' &&
            selectedMove.from &&
            selectedMove.to &&
            selectedMove.from[0] !== selectedMove.to[0] &&
            !sanLabel.includes('x')
          ) {
            sanLabel = `${selectedMove.from[0]}x${selectedMove.to}`;
            if (selectedMove.promotion) {
              sanLabel += `=${(selectedMove.promotion as string).toUpperCase()}`;
            }
          }
          rawLabel = (selectedMove.lan || selectedMove.san || rawLabel) as string;
        }

        return { move: sanLabel, score: entry.score, rank: idx + 1, raw: rawLabel };
      });
      // Find user's rank - either from sortedTop (if in top 5) or calculate from full scores map
      const userRankInTop5 = sortedTop.find((e) => e.raw === userMoveKey)?.rank;
      let userRank;
      if (userRankInTop5) {
        userRank = userRankInTop5;
      } else {
        // Not in top 5, need to find the rank in the full list
        const allSortedMoves = Object.entries(scoresMap)
          .map(([k, v]) => ({ move: k, score: Number(v) }))
          .sort((a, b) => b.score - a.score);
        const userMoveIndex = allSortedMoves.findIndex((m) => m.move === userMoveKey);
        userRank = userMoveIndex >= 0 ? userMoveIndex + 1 : 999; // 999 means not found
      }

      const newHistory: any = [
        ...history.slice(0, currentMove),
        { san: move.san, source: sourceSquare, target: targetSquare }
      ];
      setHistory(newHistory);
      setCurrentMove(newHistory.length);
      setPosition(chess.current.fen());
      setHighlightedSquares({
        [sourceSquare]: { backgroundColor: 'rgba(255, 255, 0, 0.4)' },
        [targetSquare]: { backgroundColor: 'rgba(255, 255, 0, 0.4)' }
      });

      const elapsedTime = timer.time;
      const timeStr = `${Math.floor(timer.time / 60)}:${timer.time % 60 < 10 ? `0${timer.time % 60}` : timer.time % 60}`;

      // İlk hamle yapıldığında puan hesapla ve kaydet, sonraki hamlelerde sadece görsel güncelleme yap
      if (!puzzleScored) {
        // İlk hamle - puan hesapla ve kaydet
        // 🔒 FIX: UserData_${username} key'inden oku (puzzleScores burada doğru)
        const userDataKey = `UserData_${user.username}`;
        const userDataRaw = localStorage.getItem(userDataKey) || localStorage.getItem('UserData');
        if (userDataRaw) {
          const parsed = JSON.parse(userDataRaw);
          const prevHistory: any[] = Array.isArray(parsed.history) ? parsed.history : [];

          // Puzzle puanlarını sakla (puzzle ID -> score delta)
          const puzzleScores = parsed.puzzleScores || {};
          // Puzzle time'larını sakla (puzzle ID -> time in seconds) - sadece puanı olan puzzle'lar için
          const puzzleTimes = parsed.puzzleTimes || {};
          // Puzzle çözülme zamanlarını sakla (puzzle ID -> Unix timestamp in seconds)
          const puzzleSolvedAt = parsed.puzzleSolvedAt || {};
          // currentPuzzleData has capitalized keys (Id, Fen, ...). Use Id for key.
          if (currentPuzzleData && (currentPuzzleData as any).Id) {
            puzzleScores[(currentPuzzleData as any).Id] = delta;
            puzzleTimes[(currentPuzzleData as any).Id] = elapsedTime; // Sadece puanı olan puzzle'ların time'ını sakla
            puzzleSolvedAt[(currentPuzzleData as any).Id] = Math.floor(Date.now() / 1000); // Unix timestamp (seconds)
          }

          // Sadece puanı olan puzzle'ları sayarak average score hesapla
          const scores = Object.values(puzzleScores).filter(
            (score): score is number => score !== undefined && score !== null && !Number.isNaN(score) && typeof score === 'number'
          );
          const oldCount = scores.length; // Sadece puanı olan puzzle sayısı
          const newCount = oldCount; // Yeni puzzle zaten puzzleScores'a eklendi

          // Sadece puanı olan puzzle'ların time'ını topla
          const totalTime = Object.values(puzzleTimes).reduce((acc: number, time) => acc + (typeof time === 'number' ? time : 0), 0);
          const newTime = totalTime;

          // Toplam puanı hesapla: sadece puanı olan puzzle'ların toplamı
          const totalPoints: number = scores.reduce((acc, score) => acc + score, 0);
          // Yeni ortalama = toplam / count
          const newScore = newCount > 0 ? Number((totalPoints / newCount).toFixed(3)) : 0;

          // Verileri güncelle
          const nextHistory = [...prevHistory, { t: Date.now(), score: newScore }].slice(-50);
          const updatedUserData = {
            ...parsed,
            count: newCount,
            time: newTime,
            score: newScore,
            history: nextHistory,
            puzzleScores,
            puzzleTimes,
            puzzleSolvedAt
          };

          // localStorage'ı güncelle - HER İKİ KEY'E DE YAZ
          localStorage.setItem(`UserData_${user.username}`, JSON.stringify(updatedUserData));
          localStorage.setItem('UserData', JSON.stringify(updatedUserData));

          // 🔒 ATOMİK YAZIM (#1) + AWAIT/RETRY (#2):
          // solvedPuzzles + puzzleScores + puzzleTimes + puzzleSolvedAt tek
          // update'te, DB ile birleştirilerek ve retry ile yazılır. Yazım bitene
          // kadar Next butonu kilitli kalır (aynı kullanıcıda art arda çözümde
          // oluşan lost-update yarışını da pratikte engeller).
          const solvedPuzzleId = (currentPuzzleData as any)?.Id;
          if (solvedPuzzleId) {
            (async () => {
              const result = await amplifyService.recordSolvedPuzzle({
                userId: parsed.id || user.username,
                puzzleId: solvedPuzzleId,
                move: userMoveKey, // sunucu skoru bu hamleden OTORİTER hesaplar
                delta, // sadece offline fallback / anlık gösterim için
                elapsedTime,
                solvedAtSeconds: Math.floor(Date.now() / 1000),
                localScores: puzzleScores,
                localTimes: puzzleTimes,
                localSolvedAt: puzzleSolvedAt,
              });

              // Dönen birleşmiş (merged) veriyle localStorage'ı TAZE okuyarak senkronla.
              try {
                const key = `UserData_${user.username}`;
                const freshRaw = localStorage.getItem(key);
                const base = freshRaw ? JSON.parse(freshRaw) : updatedUserData;
                const synced = {
                  ...base,
                  solvedPuzzles: result.solvedPuzzles,
                  puzzleScores: result.puzzleScores,
                  puzzleTimes: result.puzzleTimes,
                  puzzleSolvedAt: result.puzzleSolvedAt,
                  score: result.score,
                  count: result.count,
                  time: result.time,
                };
                localStorage.setItem(key, JSON.stringify(synced));
                localStorage.setItem('UserData', JSON.stringify(synced));
                setUserData(synced);
                setSolvedPuzzles((result.solvedPuzzles || []).filter((p: any) => p !== null));
              } catch (e) {
                console.error('Error syncing localStorage after solve:', e);
              }

              if (result.success) {
                console.log('Puzzle solve persisted atomically.');
              } else {
                // DB'ye yazılamadı: veri localStorage'da duruyor, bir sonraki
                // çözümde otomatik merge edilip tekrar denenecek. Kullanıcıyı uyar.
                console.error('⚠️ Puzzle çözümü DB\'ye yazılamadı (retry sonrası).');
                setAlertMessage('Puanın kaydedilemedi (bağlantı sorunu olabilir). Verin cihazında saklandı ve bir sonraki çözümde tekrar gönderilecek.');
                setShowAlertModal(true);
              }

              // Yazım tamamlandı (başarılı/başarısız) — Next'i aç.
              setNextButtonDisabled(false);
            })();
          } else {
            // Puzzle ID yoksa kaydedilecek bir şey yok; buton takılı kalmasın.
            setNextButtonDisabled(false);
          }
        } else {
          // userDataRaw yoksa (teorik) yazamayız; Next takılı kalmasın.
          setNextButtonDisabled(false);
        }

        // Update puzzle move stats
        if (currentPuzzleData && (currentPuzzleData as any).Id) {
          const moveSan = (move as any).san || (() => {
            const piece = move.piece.toUpperCase();
            return piece === 'P' ? move.to : `${piece}${move.to}`;
          })();
          amplifyService.updatePuzzleMoveStats((currentPuzzleData as any).Id, moveSan);
        }

        // İlk hamle yapıldı, flag'i set et ve puan bilgilerini sakla
        setPuzzleScored(true);
        // NOT: Next butonu burada AÇILMAZ. DB yazımı (recordSolvedPuzzle)
        // tamamlanınca yukarıdaki persist bloğunda açılır.
        setFirstMoveScore({
          score: userMoveScore,
          scoreChange: delta,
          time: timeStr
        });

        // Puzzle çözüldü, solvedPuzzles array'ına ekle
        if (currentPuzzleData && (currentPuzzleData as any).Id) {
          addSolvedPuzzleLocally((currentPuzzleData as any).Id);
        }
      }

      // Hamleyi SAN formatına çevir (her hamle için görsel güncelleme)
      const moveSan = (move as any).san || (() => {
        const piece = move.piece.toUpperCase();
        return piece === 'P' ? move.to : `${piece}${move.to}`;
      })();

      // previousTotalScore için localStorage'dan al (ilk hamle değilse puzzleStartScore kullan)
      let previousScore: number | undefined = puzzleStartScore ?? undefined;
      if (previousScore === undefined) {
        const userDataForScore = localStorage.getItem(`UserData_${user.username}`) || localStorage.getItem('UserData');
        if (userDataForScore) {
          const parsedForScore = JSON.parse(userDataForScore);
          previousScore = parsedForScore.score || 0;
        } else {
          previousScore = 0;
        }
      }

      // İlk hamle yapıldıysa, saklanan puan bilgilerini kullan; değilse yeni hesaplanan değerleri kullan
      const displayScore = firstMoveScore ? firstMoveScore.score : userMoveScore;
      const displayScoreChange = firstMoveScore ? firstMoveScore.scoreChange : delta;
      const displayTime = firstMoveScore ? firstMoveScore.time : timeStr;

      setMoveInfo({
        san: moveSan,
        score: displayScore,
        time: displayTime,
        scoreChange: displayScoreChange,
        previousTotalScore: previousScore,
        // @ts-ignore extra info
        bestMove: bestMoveKey,
        // @ts-ignore
        bestScore,
        topMoves: sortedTop,
        userRank,
        userMoveKey,
        userMoveSan: moveSan
      });

      // Eğer kullanıcının hamlesi 1. sırada değilse, en iyi hamleyi arrow ile göster
      if (userRank !== 1 && bestMoveKey && bestMoveKey.length === 4) {
        const fromSquare = bestMoveKey.slice(0, 2) as Square;
        const toSquare = bestMoveKey.slice(2, 4) as Square;
        console.log('Setting best move arrow:', { fromSquare, toSquare, userRank, bestMoveKey });
        setBestMoveArrow([[fromSquare, toSquare]]);
      } else {
        console.log('Not setting arrow:', { userRank, bestMoveKey, bestMoveKeyLength: bestMoveKey?.length });
        setBestMoveArrow([]);
      }

      // Timer'ı temizle (ref ile)
      if (timerIntervalRef.current !== null) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      if (timer.intervalId !== null) {
        clearInterval(timer.intervalId);
      }
    } else {
      const newHistory: any = [
        ...history.slice(0, currentMove),
        { san: move.san, source: sourceSquare, target: targetSquare }
      ];
      setHistory(newHistory);
      setCurrentMove(newHistory.length);
      setPosition(chess.current.fen());
      setHighlightedSquares({
        [sourceSquare]: { backgroundColor: 'rgba(255, 255, 0, 0.4)' },
        [targetSquare]: { backgroundColor: 'rgba(255, 255, 0, 0.4)' }
      });
    }
  }, [timer, currentPuzzleData, puzzleScored, history, currentMove, puzzleStartScore, firstMoveScore, addSolvedPuzzleLocally]);

  const onPieceDrop = (sourceSquare: Square, targetSquare: Square): boolean => {
    try {
      const preFen = chess.current.fen();
      const move = chess.current.move({
        from: sourceSquare,
        to: targetSquare,
        promotion: 'q'
      });

      if (move) {
        handlePuzzleMove(sourceSquare, targetSquare, move, preFen);
        return true;
      }
    } catch (error) {
      setSelectedSquare(null);
    }
    return false;
  };

  const handleSquareClick = (square: any) => {
    if (selectedSquare) {
      const possibleMoves = chess.current.moves({ square: selectedSquare, verbose: true });
      const isValidMove = possibleMoves.some(move => move.to === square);

      if (isValidMove) {
        const preFen = chess.current.fen();
        const move = chess.current.move({
          from: selectedSquare,
          to: square,
          promotion: 'q'
        });

        if (move) {
          // Puzzle durumunda handlePuzzleMove'u kullan, değilse normal mantığı kullan
          if (currentPuzzleData) {
            handlePuzzleMove(selectedSquare, square, move, preFen);
          } else {
            const newHistory: any = [
              ...history.slice(0, currentMove),
              { san: move.san, source: selectedSquare, target: square }
            ];
            setHistory(newHistory);
            setCurrentMove(newHistory.length);
            setPosition(chess.current.fen());
            setHighlightedSquares({
              [selectedSquare]: { backgroundColor: 'rgba(255, 255, 0, 0.4)' },
              [square]: { backgroundColor: 'rgba(255, 255, 0, 0.4)' }
            });
          }
          setSelectedSquare(null);
        }
      } else {
        const piece = chess.current.get(square);
        if (piece && piece.color === chess.current.turn()) {
          setSelectedSquare(square);
          setHighlightedSquares({
            [square]: { backgroundColor: '#537056' }
          });
        } else {
          setSelectedSquare(null);
          setHighlightedSquares({});
        }
      }
    } else {
      const piece = chess.current.get(square);
      if (piece && piece.color === chess.current.turn()) {
        setSelectedSquare(square);
        setHighlightedSquares({
          [square]: { backgroundColor: '#537056' }
        });
      }
    }
  };

  const getNextPuzzle = useCallback((dbSolvedPuzzles: string[] = []) => {
    if (puzzleCache.length === 0) {
      return null;
    }

    // Cache'den henüz çözülmemiş ilk puzzle'ı bul
    const unsolvedPuzzle = puzzleCache.find(p => {
      const fen = (p as any).fen;
      return !dbSolvedPuzzles.includes(p.id) && (!fen || !dbSolvedPuzzles.includes(fen));
    });

    if (!unsolvedPuzzle) {
      return null;
    }

    // Bulunan puzzle'ı cache'den çıkar
    setPuzzleCache(prev => {
      const updated = prev.filter(p => p.id !== unsolvedPuzzle.id);

      // Puzzle çıkarıldıktan sonra cache boyutu 5'ten azsa, yeni puzzle'lar yükle
      if (updated.length < 5) {
        loadPuzzleBatch();
      }

      return updated;
    });

    return unsolvedPuzzle;
  }, [puzzleCache, loadPuzzleBatch]);

  const startPuzzleGame = useCallback((puzzle: any) => {
    if (!puzzle) return;

    // Scores'u parse et ve düzenle
    const parsedScores = typeof puzzle.scores === 'string'
      ? JSON.parse(puzzle.scores)
      : puzzle.scores;

    const formattedPuzzle: PuzzleData = {
      Id: puzzle.id,
      Fen: (puzzle as any).fen,
      WhitePlayer: puzzle.whitePlayer,
      BlackPlayer: puzzle.blackPlayer,
      Date: puzzle.date,
      MoveIndex: puzzle.moveIndex,
      Turn: puzzle.turn,
      Scores: parsedScores
    };

    // Puzzle başlamadan önce mevcut total score'u sakla
    const userDataForStart = localStorage.getItem(`UserData_${user.username}`) || localStorage.getItem('UserData');
    if (userDataForStart) {
      const parsedForStart = JSON.parse(userDataForStart);
      setPuzzleStartScore(parsedForStart.score || 0);
    } else {
      setPuzzleStartScore(0);
    }

    setCurrentPuzzleData(formattedPuzzle);
    // Use FEN from field, but keep backward-compat if older records stored FEN in id
    const fen = (puzzle as any).fen || puzzle.id;
    setInitialPuzzleFen(fen);
    setPosition(fen);
    chess.current.load(fen);
    setOrientation(getOrientationFromFen(fen));
    setPuzzleScored(false);
    setFirstMoveScore(null);
    setMoveInfo(null);
    setBestMoveArrow([]);
    setHistory([]);
    setCurrentMove(0);
    setHighlightedSquares({});
    startTimer();
  }, [getOrientationFromFen]);

  const handleTestClick = useCallback(async () => {
    if (allPuzzles.length === 0) {
      setLoadingPuzzles(true);
      await loadPuzzleBatch();
      setLoadingPuzzles(false);
      // If puzzles loaded successfully, show modal
      if (allPuzzles.length > 0 || availableDates.length > 0) {
        setShowDateSelectionModal(true);
      } else {
        // Maybe show a toast or alert if no puzzles found?
        // For now, just rely on the fact that loadPuzzleBatch handles errors
        // But we should probably check if we actually got puzzles
        setShowDateSelectionModal(true); // Let the modal show empty state or handle it
      }
    } else {
      setShowDateSelectionModal(true);
    }
  }, [allPuzzles.length, loadPuzzleBatch, availableDates.length]);

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
    setShowDateSelectionModal(false);

    // Filter puzzles for this date
    const filtered = allPuzzles.filter(p => {
      if (!(p as any).createdAt) return false;
      const pDate = new Date((p as any).createdAt).toLocaleDateString('tr-TR');
      return pDate === date;
    });

    // Sort by createdAt (oldest to newest within that day)
    filtered.sort((a, b) => {
      const dateA = (a as any).createdAt ? new Date((a as any).createdAt).getTime() : 0;
      const dateB = (b as any).createdAt ? new Date((b as any).createdAt).getTime() : 0;
      return dateA - dateB;
    });

    setPuzzleCache(filtered);

    // Start the first puzzle immediately
    if (filtered.length > 0) {
      const firstPuzzle = filtered[0];
      startPuzzleGame(firstPuzzle);
      // Remove it from cache effectively so next button works correctly
      setPuzzleCache(filtered.slice(1));
    }
  };

  const startTimer = () => {
    // Önce mevcut timer'ı mutlaka temizle (ref ile)
    if (timerIntervalRef.current !== null) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    // State'teki timer'ı da temizle
    setTimer(prevTimer => {
      if (prevTimer.intervalId !== null) {
        clearInterval(prevTimer.intervalId);
      }
      return { time: 0, intervalId: null };
    });

    // Next butonunu disabled yap (sadece puzzle çözüldüğünde aktif olacak)
    setNextButtonDisabled(true);

    // Yeni timer başlat
    const intervalId = window.setInterval(() => {
      setTimer(currentTimer => {
        const newTime = currentTimer.time + 1;
        return { ...currentTimer, time: newTime };
      });
    }, 1000);

    // Ref'e kaydet
    timerIntervalRef.current = intervalId;

    // State'e de kaydet
    setTimer({ time: 0, intervalId });
  };

  const handleCloseModal = () => {
    setShowMoveModal(false);
  };

  const handleNext = useCallback(async () => {
    // Eğer zaten bir next işlemi devam ediyorsa veya buton disabled ise, yeni isteği engelle
    if (isLoadingNext || nextButtonDisabled) {
      return;
    }

    setIsLoadingNext(true);

    // Timer'ı temizle (ref ile)
    if (timerIntervalRef.current !== null) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    // State'teki timer'ı da temizle
    setTimer(prevTimer => {
      if (prevTimer.intervalId !== null) {
        clearInterval(prevTimer.intervalId);
      }
      return { time: 0, intervalId: null };
    });

    try {
      // DB'den live solved puzzles çek
      let dbSolvedPuzzles: string[] = [];
      if (user) {
        try {
          const dbUser = await amplifyService.getUser(user.username);
          if (dbUser && dbUser.solvedPuzzles) {
            dbSolvedPuzzles = dbUser.solvedPuzzles.filter((p: any) => p !== null) as string[];
          }
        } catch (error) {
          console.error("Error fetching solved puzzles from DB:", error);
        }
      }

      // Önbellekten puzzle al
      let newPuzzle = getNextPuzzle(dbSolvedPuzzles);

      // Eğer önbellekte puzzle yoksa, doğrudan getir
      if (!newPuzzle) {
        // Cache'de yoksa, DB'den random puzzle çek ve solved olmayanları bul
        let attempts = 0;
        while (attempts < 20) {
          newPuzzle = await amplifyService.getRandomPuzzle();
          if (newPuzzle && !dbSolvedPuzzles.includes(newPuzzle.id) && (!(newPuzzle as any).fen || !dbSolvedPuzzles.includes((newPuzzle as any).fen))) {
            break;
          }
          attempts++;
        }
      }

      if (newPuzzle && !dbSolvedPuzzles.includes(newPuzzle.id) && (!(newPuzzle as any).fen || !dbSolvedPuzzles.includes((newPuzzle as any).fen))) {
        // Scores'u parse et ve düzenle
        const parsedScores = typeof newPuzzle.scores === 'string'
          ? JSON.parse(newPuzzle.scores)
          : newPuzzle.scores;
        console.log(parsedScores, "parsedScores");
        const formattedPuzzle: PuzzleData = {
          Id: newPuzzle.id,
          Fen: (newPuzzle as any).fen,
          WhitePlayer: newPuzzle.whitePlayer,
          BlackPlayer: newPuzzle.blackPlayer,
          Date: newPuzzle.date,
          MoveIndex: newPuzzle.moveIndex,
          Turn: newPuzzle.turn,
          Scores: parsedScores
        };

        // Puzzle başlamadan önce mevcut total score'u sakla
        const userDataNext = localStorage.getItem(`UserData_${user.username}`) || localStorage.getItem('UserData');
        if (userDataNext) {
          const parsedNext = JSON.parse(userDataNext);
          setPuzzleStartScore(parsedNext.score || 0);
        } else {
          setPuzzleStartScore(0);
        }

        setCurrentPuzzleData(formattedPuzzle);
        const fen = (newPuzzle as any).fen || newPuzzle.id;
        setInitialPuzzleFen(fen);
        setPosition(fen);
        chess.current.load(fen);
        setOrientation(getOrientationFromFen(fen));
        setPuzzleScored(false);
        setFirstMoveScore(null);
        setMoveInfo(null);
        setBestMoveArrow([]);
        setHistory([]);
        setCurrentMove(0);
        setHighlightedSquares({});
        startTimer();
      } else {
        // Yeni puzzle bulunamadıysa başlangıç pozisyonuna dön
        setPosition('start');
        chess.current.reset();
        setHistory([]);
        setCurrentMove(0);
        setHighlightedSquares({});
        setAlertMessage('No new puzzles available');
        setShowAlertModal(true);
      }
    } catch (error) {
      console.error('Error fetching puzzle:', error);
      setPosition('start');
      chess.current.reset();
      setHistory([]);
      setCurrentMove(0);
      setHighlightedSquares({});
      setAlertMessage('Error loading puzzle');
      setShowAlertModal(true);
    } finally {
      // Reset moveInfo when moving to next puzzle
      setFirstMoveScore(null);
      setMoveInfo(null);
      setIsLoadingNext(false);
    }
  }, [solvedPuzzles, getNextPuzzle, addSolvedPuzzleLocally, isLoadingNext, nextButtonDisabled]);

  const applySuggestedMove = useCallback((lan: string) => {
    try {
      const fen = initialPuzzleFen || currentPuzzleData?.Fen || currentPuzzleData?.Id || 'start';
      chess.current.load(fen);
      const from = lan.slice(0, 2) as string;
      const to = lan.slice(2, 4) as string;
      chess.current.move({ from, to, promotion: 'q' } as any);
      const histItem: any = { san: lan, source: from, target: to };
      setHistory([histItem]);
      setCurrentMove(1);
      setPosition(chess.current.fen());
      setHighlightedSquares({
        [from]: { backgroundColor: 'rgba(255, 255, 0, 0.4)' },
        [to]: { backgroundColor: 'rgba(255, 255, 0, 0.4)' }
      });
    } catch (e) {
      console.error('Failed to apply suggested move', e);
    }
  }, [initialPuzzleFen, currentPuzzleData]);

  const goToMoveIndex = useCallback((targetIndex: number) => {
    try {
      const baseFen = initialPuzzleFen || currentPuzzleData?.Fen || currentPuzzleData?.Id || 'start';
      const tmp = new Chess();
      tmp.load(baseFen as string);
      for (let i = 0; i < Math.min(targetIndex, history.length); i++) {
        const h: any = history[i];
        tmp.move({ from: h.source, to: h.target, promotion: 'q' } as any);
      }
      chess.current.load(tmp.fen());
      setPosition(tmp.fen());
      setCurrentMove(Math.min(targetIndex, history.length));
      if (targetIndex > 0 && history[targetIndex - 1]) {
        const last: any = history[targetIndex - 1];
        setHighlightedSquares({
          [last.source]: { backgroundColor: 'rgba(255, 255, 0, 0.4)' },
          [last.target]: { backgroundColor: 'rgba(255, 255, 0, 0.4)' }
        });
      } else {
        setHighlightedSquares({});
      }
    } catch (e) {
      console.error('Failed to go to move index', e);
    }
  }, [initialPuzzleFen, currentPuzzleData, history]);

  // FEN'i Lichess analiz URL formatına çevir
  const getLichessAnalysisUrl = useCallback((fen: string): string => {
    // FEN'i boşluklara göre ayır
    const parts = fen.split(' ');
    if (parts.length === 0) return '';

    // İlk kısım board pozisyonu (zaten / ile ayrılmış)
    const boardPosition = parts[0];

    // Geri kalan kısımları _ ile birleştir
    const rest = parts.slice(1).join('_');

    // Eğer rest varsa, boardPosition_rest formatında birleştir
    const fenForUrl = rest ? `${boardPosition}_${rest}` : boardPosition;

    return `https://lichess.org/analysis/standard/${fenForUrl}`;
  }, []);

  const boardStyle = {
    borderRadius: '5px',
    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.5)',
    border: '2px solid #2b3a46',
  };

  const squareStyles = {
    // Blue2 theme (custom)
    light: { backgroundColor: '#849CB0' },
    dark: { backgroundColor: '#637A8C' }
  };

  // Component mount olduğunda user bilgilerini konsola yazdır
  useEffect(() => {

  }, [user]);

  return (
    <>
      <div className="Board">
        <div style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          width: "100%",
          paddingBottom: "2rem",
          justifyContent: "center",
          alignItems: isMobile ? "center" : "stretch"
        }}>
          {/* Sol taraf - Search Game ve Puzzles */}
          <div className="lg:flex hidden" style={{
            marginRight: '20px',
            flexDirection: 'column',
            width: '250px',
            alignSelf: isMobile ? 'auto' : 'stretch',
            justifyContent: 'space-between',
            gap: '1.5rem'
          }}>
            {/* Search Game Container */}
            <div className="search-game" style={{
              background: 'linear-gradient(135deg, #1a1f25 0%, #2d3748 100%)',
              color: "floralwhite",
              flex: 1,
              border: '1px solid rgba(255,255,255,0.1)',
              padding: '1.5rem',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              borderRadius: '12px'
            }}>
              {currentPuzzleData ? (
                // Puzzle mode: Show Move Information
                <div>
                  <span style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    color: '#a3bffa',
                    display: 'block',
                    marginBottom: '1rem'
                  }}>Move Information</span>

                  {(() => {
                    // Puzzle başladığında scores'dan top 5'i göster (hamleler gizli)
                    // Hamle yapıldıktan sonra moveInfo'dan göster (hamleler açık)
                    let movesToShow: Array<{ rank: number; move: string; score: number; raw?: string }> = [];

                    if (moveInfo && moveInfo.topMoves) {
                      // Hamle yapıldı, gerçek hamleleri göster
                      movesToShow = moveInfo.topMoves.slice(0, 5);
                    }

                    if (movesToShow.length > 0 && moveInfo) {
                      return (
                        <div style={{
                          marginBottom: '1rem',
                          padding: '0.5rem 0',
                        }}>
                          <div style={{ color: '#a3bffa', marginBottom: '0.5rem', textAlign: 'left', fontSize: '0.95rem', fontWeight: 600 }}>
                            Top 5 moves
                          </div>
                          <ul style={{ listStyle: 'none', padding: 0, margin: 0, textAlign: 'left' }}>
                            {movesToShow.map((m) => {
                              const isUserMove = moveInfo && moveInfo.userMoveKey && (m.raw === moveInfo.userMoveKey);
                              const displayMove = isUserMove && moveInfo.userMoveSan ? moveInfo.userMoveSan : m.move;

                              return (
                                <li
                                  key={m.rank}
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '6px 10px',
                                    fontSize: '0.9rem',
                                    color: '#e5e7eb',
                                    background: isUserMove ? 'rgba(99,179,237,0.12)' : 'transparent',
                                    fontWeight: isUserMove ? 700 : 500,
                                    borderRadius: '6px',
                                    marginBottom: '4px',
                                    border: isUserMove ? '1px solid rgba(99,179,237,0.6)' : '1px solid transparent'
                                  }}
                                >
                                  <span>
                                    <span style={{ color: '#a3bffa', marginRight: 6 }}>#{m.rank}</span>
                                    {displayMove}
                                  </span>
                                  <span style={{ color: '#e2e8f0' }}>
                                    {formatMoveScore(m.score)}
                                  </span>
                                </li>
                              );
                            })}
                            {/* Eğer kullanıcının hamlesi top 5'te değilse ayrı göster */}
                            {moveInfo && moveInfo.userRank && moveInfo.userRank > 5 && moveInfo.userMoveKey && (
                              <li style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '6px 10px',
                                fontSize: '0.9rem',
                                color: '#e5e7eb',
                                background: 'rgba(99,179,237,0.12)',
                                fontWeight: 700,
                                borderRadius: '6px',
                                marginBottom: '4px',
                                border: '1px solid rgba(99,179,237,0.6)'
                              }}>
                                <span>
                                  <span style={{ color: '#a3bffa', marginRight: 6 }}>#{moveInfo.userRank}</span>
                                  {moveInfo.userMoveSan || moveInfo.userMoveKey}
                                </span>
                                <span style={{ color: '#e2e8f0' }}>
                                  {formatMoveScore(moveInfo.score ?? 0)}
                                </span>
                              </li>
                            )}
                          </ul>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {moveInfo ? (
                    <>
                      <div style={{
                        marginBottom: '1rem',
                        padding: '0.5rem 0',
                        borderTop: '1px solid rgba(255,255,255,0.1)'
                      }}>
                        <p style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: '0.5rem',
                          fontSize: '0.9rem'
                        }}>
                          <strong>Score:</strong>
                          <span style={{
                            color: (() => {
                              if (moveInfo.bestScore !== undefined && moveInfo.score !== undefined) {
                                const scoreDiff = (moveInfo.bestScore - moveInfo.score) * 100;
                                if (scoreDiff >= 0 && scoreDiff < 3) return '#68d391'; // yeşil
                                if (scoreDiff >= 3 && scoreDiff < 10) return '#f6ad55'; // turuncu
                                return '#f56565'; // kırmızı (10 ve üstü)
                              }
                              return '#f6ad55'; // varsayılan turuncu
                            })()
                          }}>
                            {moveInfo.bestScore !== undefined && moveInfo.score !== undefined
                              ? ((moveInfo.bestScore - moveInfo.score) * 100).toFixed(1)
                              : 'N/A'}
                          </span>
                        </p>
                      </div>
                    </>
                  ) : null}
                </div>
              ) : (
                // Normal mode: Show Search Game
                <>
                  <span style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    color: '#a3bffa',
                    display: 'block',
                    marginBottom: '1rem'
                  }}>Search Game</span>
                  <div className="mt-4">
                    <input
                      type="text"
                      placeholder="Enter player name..."
                      style={{
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: '#fff',
                        padding: '0.5rem',
                        borderRadius: '8px',
                        width: '100%',
                        marginBottom: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.2s ease',
                        fontSize: '0.9rem',
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#6366f1'}
                      onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                    <Button
                      variant="primary"
                      className="w-full"
                      onClick={handleSearchClick}
                      disabled={loadingSearch}
                      style={{
                        background: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        boxShadow: 'none',
                        padding: '0.4rem',
                        fontSize: '0.85rem',
                        opacity: loadingSearch ? 0.7 : 1,
                        cursor: loadingSearch ? 'not-allowed' : 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        if (!loadingSearch) {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)';
                          e.currentTarget.style.filter = 'brightness(1.15)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!loadingSearch) {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)';
                          e.currentTarget.style.filter = 'brightness(1)';
                        }
                      }}
                    >
                      {loadingSearch ?
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> :
                        <>
                          <FaSearch /> Search
                        </>
                      }
                    </Button>
                  </div>

                  {searchInput && searchPerformed && (
                    <label className='block mt-8 italic font-extralight'>
                      {games.length === 0 ? 'No games found' : `${games.length} game${games.length > 1 ? 's' : ''} found`}
                    </label>
                  )}
                  <ul className='games' style={{ listStyleType: 'none', padding: 0, marginTop: '10px' }}>
                    {games.map((game: { white: string, black: string, moves: string }, index) => (
                      <li
                        key={index}
                        onClick={() => handleGameClick(game)}
                        className='text-[#DEB887] font-normal'
                        style={{
                          backgroundColor: '#2c3e50',
                          padding: '5px 12px',
                          marginBottom: '5px',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          transition: 'background-color 0.3s',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#34495e')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#2c3e50')}
                      >
                        {capitalizeFirstLetter(game.white)} - {capitalizeFirstLetter(game.black)}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            {/* Puzzles/Next Button - Container dışında */}
            <Button
              className='btn-font-size w-full'
              variant="primary"
              onClick={currentPuzzleData ? handleNext : handleTestClick}
              disabled={currentPuzzleData ? (nextButtonDisabled || isLoadingNext) : loadingPuzzles}
              style={{
                background: currentPuzzleData ? 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)' : 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                border: 'none',
                opacity: (currentPuzzleData && (nextButtonDisabled || isLoadingNext)) || loadingPuzzles ? 0.5 : 1,
                cursor: (currentPuzzleData && (nextButtonDisabled || isLoadingNext)) || loadingPuzzles ? 'not-allowed' : 'pointer',
                padding: '0.4rem 1rem',
                borderRadius: '8px',
                transition: 'all 0.3s ease',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                fontWeight: '600',
                boxShadow: 'none',
              }}
              onMouseEnter={(e) => {
                if (!loadingPuzzles && !(currentPuzzleData && (nextButtonDisabled || isLoadingNext))) {
                  e.currentTarget.style.filter = 'brightness(1.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loadingPuzzles && !(currentPuzzleData && (nextButtonDisabled || isLoadingNext))) {
                  e.currentTarget.style.filter = 'brightness(1)';
                }
              }}
            >
              {currentPuzzleData ? (
                <>
                  {isLoadingNext ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : <FaArrowRight />} Next
                </>
              ) : (
                <>
                  {loadingPuzzles ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : <FaPuzzlePiece style={{ marginTop: '-2px' }} />} Puzzles
                </>
              )}
            </Button>
          </div>
          <div className="chessboard-container">
            <Chessboard
              position={position}
              onPieceDrop={onPieceDrop}
              onSquareClick={handleSquareClick}
              animationDuration={0}
              boardOrientation={orientation}
              customBoardStyle={boardStyle}
              customLightSquareStyle={squareStyles.light}
              customDarkSquareStyle={squareStyles.dark}
              customSquareStyles={highlightedSquares}
              customDropSquareStyle={{ boxShadow: 'none' }}
              customArrows={bestMoveArrow}
            />
          </div>

          {/* Mobile Controls & History Tabs */}
          {isMobile && (
            <div style={{
              width: '100%',
              maxWidth: '100%',
              marginTop: '1rem',
              background: 'linear-gradient(135deg, #0f172a 0%, #1f2937 100%)',
              borderRadius: '12px',
              border: '1px solid rgba(255,255,255,0.1)',
              overflow: 'hidden',
              padding: '0'
            }}>
              {/* Tab Navigation */}
              <div style={{
                display: 'flex',
                borderBottom: '1px solid rgba(255,255,255,0.1)'
              }}>
                <button
                  onClick={() => setActiveTab('controls')}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: activeTab === 'controls'
                      ? 'linear-gradient(135deg, rgba(79,70,229,.3) 0%, rgba(6,182,212,.3) 100%)'
                      : 'transparent',
                    border: 'none',
                    color: '#e5e7eb',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Controls
                </button>
                <button
                  onClick={() => setActiveTab('history')}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: activeTab === 'history'
                      ? 'linear-gradient(135deg, rgba(79,70,229,.3) 0%, rgba(6,182,212,.3) 100%)'
                      : 'transparent',
                    border: 'none',
                    color: '#e5e7eb',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  History
                </button>
              </div>

              {/* Tab Content */}
              <div style={{ padding: '1rem' }}>
                {activeTab === 'controls' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {/* Timer (only show during puzzle) */}
                    {currentPuzzleData && (
                      <div style={{
                        background: 'linear-gradient(135deg, #1a1f25 0%, #2d3748 100%)',
                        color: "#fff",
                        padding: '0.9rem',
                        fontFamily: "monospace",
                        fontSize: "1.2rem",
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        borderRadius: '10px',
                        border: '1px solid rgba(255,255,255,0.1)',
                        textAlign: 'center',
                        letterSpacing: '2px'
                      }}>
                        <span style={{ color: '#a3bffa', fontSize: '0.85rem', display: 'block', marginBottom: '0.35rem' }}>
                          Time Elapsed
                        </span>
                        {Math.floor(timer.time / 60)}:{timer.time % 60 < 10 ? `0${timer.time % 60}` : timer.time % 60}
                      </div>
                    )}

                    {/* Control Buttons */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
                      <Button
                        variant="secondary"
                        onClick={handleBackMove}
                        disabled={currentMove === 0}
                        style={{
                          background: 'rgba(255,255,255,0.1)',
                          border: 'none',
                          padding: '0.6rem',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#e5e7eb'
                        }}
                      >
                        <FaStepBackward />
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => setOrientation(prev => (prev === 'white' ? 'black' : 'white'))}
                        style={{
                          background: 'rgba(255,255,255,0.1)',
                          border: 'none',
                          padding: '0.6rem',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#e5e7eb'
                        }}
                      >
                        <HiSwitchHorizontal style={{ transform: 'rotate(90deg)' }} />
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={handleForwardMove}
                        disabled={currentMove === history.length}
                        style={{
                          background: 'rgba(255,255,255,0.1)',
                          border: 'none',
                          padding: '0.6rem',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#e5e7eb'
                        }}
                      >
                        <FaStepForward />
                      </Button>
                    </div>

                    {/* Save Button - Hidden in puzzle mode */}
                    {!currentPuzzleData && (
                      <Button
                        variant="primary"
                        onClick={handleSaveGame}
                        style={{
                          background: 'linear-gradient(135deg, #34d399 0%, #10b981 100%)',
                          border: 'none',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem',
                          color: '#fff',
                          boxShadow: 'none',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.filter = 'brightness(1.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.filter = 'brightness(1)';
                        }}
                      >
                        <FaSave /> Save Game
                      </Button>
                    )}

                    {/* Puzzles/Next Button */}
                    <Button
                      variant="primary"
                      onClick={currentPuzzleData ? handleNext : handleTestClick}
                      disabled={currentPuzzleData ? (nextButtonDisabled || isLoadingNext) : false}
                      style={{
                        background: currentPuzzleData ? 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)' : 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                        border: 'none',
                        opacity: (currentPuzzleData && (nextButtonDisabled || isLoadingNext)) ? 0.5 : 1,
                        cursor: (currentPuzzleData && (nextButtonDisabled || isLoadingNext)) ? 'not-allowed' : 'pointer',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        color: '#fff',
                        boxShadow: 'none',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.filter = 'brightness(1.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.filter = 'brightness(1)';
                      }}
                    >
                      {currentPuzzleData ? (
                        <>
                          <FaArrowRight /> Next
                        </>
                      ) : (
                        <>
                          <FaPuzzlePiece /> Puzzles
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {activeTab === 'history' && (
                  <div style={{
                    background: 'linear-gradient(135deg, #1a1f25 0%, #2d3748 100%)',
                    color: "#fff",
                    padding: '1rem',
                    borderRadius: '10px',
                    border: '1px solid rgba(255,255,255,0.1)',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    <span style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#a3bffa',
                      display: 'block',
                      marginBottom: '0.6rem'
                    }}>
                      Move History
                    </span>
                    <ul style={{
                      paddingLeft: "6px",
                      marginTop: "0.5rem",
                      marginBottom: "0px",
                      textAlign: "left",
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: '0.35rem',
                      paddingRight: "6px",
                    }}
                      className="custom-scrollbar">
                      {history.reduce((acc: string[][], move: { san: string }, index) => {
                        const moveNumber = Math.floor(index / 2) + 1;
                        if (index % 2 === 0) {
                          acc.push([`${moveNumber}. ${move.san}`, ""]);
                        } else {
                          acc[acc.length - 1][1] = move.san;
                        }
                        return acc;
                      }, []).map((movePair: string[], index: number) => (
                        <React.Fragment key={index}>
                          <li style={{
                            background: (index * 2 === currentMove - 1) ? 'rgba(99,179,237,0.12)' : 'rgba(255,255,255,0.05)',
                            padding: '0.35rem 0.4rem',
                            borderRadius: '6px',
                            color: '#63b3ed',
                            border: (index * 2 === currentMove - 1) ? '1px solid rgba(99,179,237,0.4)' : '1px solid transparent',
                            cursor: 'pointer',
                            fontSize: '0.85rem'
                          }} onClick={() => goToMoveIndex(index * 2 + 1)}>
                            {movePair[0]}
                          </li>
                          <li style={{
                            background: (index * 2 + 1 === currentMove - 1) ? 'rgba(104,211,145,0.12)' : 'rgba(255,255,255,0.05)',
                            padding: '0.35rem 0.4rem',
                            borderRadius: '6px',
                            color: '#68d391',
                            border: (index * 2 + 1 === currentMove - 1) ? '1px solid rgba(104,211,145,0.4)' : '1px solid transparent',
                            cursor: 'pointer',
                            fontSize: '0.85rem'
                          }} onClick={() => goToMoveIndex(index * 2 + 2)}>
                            {movePair[1]}
                          </li>
                        </React.Fragment>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Action Buttons Card */}
                {activeTab === 'history' && currentPuzzleData && puzzleScored && (
                  <div style={{
                    background: 'linear-gradient(135deg, #1a1f25 0%, #2d3748 100%)',
                    color: "#fff",
                    padding: '0.75rem',
                    borderRadius: '10px',
                    border: '1px solid rgba(255,255,255,0.1)',
                    marginTop: '0.75rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem'
                  }}>
                    {/* Add to Favorites Button */}
                    <Button
                      variant="outline-light"
                      size="sm"
                      onClick={toggleFavorite}
                      style={{
                        width: '100%',
                        background: favoritePuzzles.includes(currentPuzzleData.Id)
                          ? 'rgba(229, 62, 62, 0.25)'
                          : 'rgba(229, 62, 62, 0.1)',
                        border: favoritePuzzles.includes(currentPuzzleData.Id)
                          ? '1px solid rgba(229, 62, 62, 0.5)'
                          : '1px solid rgba(229, 62, 62, 0.3)',
                        color: favoritePuzzles.includes(currentPuzzleData.Id)
                          ? '#fc8181'
                          : '#feb2b2',
                        fontSize: '0.85rem',
                        padding: '0.4rem 0.75rem',
                        borderRadius: '6px',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        boxShadow: favoritePuzzles.includes(currentPuzzleData.Id)
                          ? '0 0 10px rgba(229, 62, 62, 0.2)'
                          : 'none'
                      }}
                      onMouseEnter={(e) => {
                        if (favoritePuzzles.includes(currentPuzzleData.Id)) {
                          e.currentTarget.style.background = 'rgba(229, 62, 62, 0.35)';
                          e.currentTarget.style.borderColor = 'rgba(229, 62, 62, 0.7)';
                          e.currentTarget.style.boxShadow = '0 0 15px rgba(229, 62, 62, 0.3)';
                        } else {
                          e.currentTarget.style.background = 'rgba(229, 62, 62, 0.2)';
                          e.currentTarget.style.borderColor = 'rgba(229, 62, 62, 0.5)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (favoritePuzzles.includes(currentPuzzleData.Id)) {
                          e.currentTarget.style.background = 'rgba(229, 62, 62, 0.25)';
                          e.currentTarget.style.borderColor = 'rgba(229, 62, 62, 0.5)';
                          e.currentTarget.style.boxShadow = '0 0 10px rgba(229, 62, 62, 0.2)';
                        } else {
                          e.currentTarget.style.background = 'rgba(229, 62, 62, 0.1)';
                          e.currentTarget.style.borderColor = 'rgba(229, 62, 62, 0.3)';
                        }
                      }}
                    >
                      {favoritePuzzles.includes(currentPuzzleData.Id) ? <FaHeart color="#fc8181" /> : <FaRegHeart color="#feb2b2" />}
                      {favoritePuzzles.includes(currentPuzzleData.Id) ? 'Favorited' : 'Add to Favorites'}
                    </Button>

                    {/* See Other's Moves Button */}
                    <Button
                      variant="outline-light"
                      size="sm"
                      onClick={handleShowStats}
                      style={{
                        width: '100%',
                        background: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        color: '#a3bffa',
                        fontSize: '0.85rem',
                        padding: '0.4rem 0.75rem',
                        borderRadius: '6px',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                        e.currentTarget.style.borderColor = 'rgba(163,191,250,0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                      }}
                    >
                      <FaUsers /> See Other's Moves
                    </Button>

                    {/* Analysis on Lichess Button */}
                    {initialPuzzleFen && (
                      <Button
                        variant="outline-light"
                        size="sm"
                        onClick={() => {
                          const url = getLichessAnalysisUrl(initialPuzzleFen);
                          window.open(url, '_blank');
                        }}
                        style={{
                          width: '100%',
                          background: 'rgba(255,255,255,0.1)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          color: '#a3bffa',
                          fontSize: '0.85rem',
                          padding: '0.4rem 0.75rem',
                          borderRadius: '6px',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                          e.currentTarget.style.borderColor = 'rgba(163,191,250,0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                        }}
                      >
                        <FaExternalLinkAlt />
                        Analysis on Lichess
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="move-history lg:flex hidden" style={{
            marginLeft: '20px',
            flexDirection: 'column',
            width: '300px',
            alignSelf: isMobile ? 'auto' : 'stretch',
            gap: '1rem'
          }}>
            {/* Timer (only show during puzzle) */}
            {currentPuzzleData && (
              <div className="timer" style={{
                background: 'linear-gradient(135deg, #1a1f25 0%, #2d3748 100%)',
                color: "#fff",
                padding: '0.9rem',
                fontFamily: "monospace",
                fontSize: "1.4rem",
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                borderRadius: '10px',
                border: '1px solid rgba(255,255,255,0.1)',
                textAlign: 'center',
                letterSpacing: '2px'
              }}>
                <span style={{ color: '#a3bffa', fontSize: '0.9rem', display: 'block', marginBottom: '0.35rem' }}>
                  Time Elapsed
                </span>
                {Math.floor(timer.time / 60)}:{timer.time % 60 < 10 ? `0${timer.time % 60}` : timer.time % 60}
              </div>
            )}

            {/* Move History */}
            <div className="move-history" style={{
              background: 'linear-gradient(135deg, #1a1f25 0%, #2d3748 100%)',
              color: "#fff",
              height: "100%",
              flex: 1,
              padding: '1rem',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              borderRadius: '10px',
              border: '1px solid rgba(255,255,255,0.1)'
            }}>
              <span style={{
                fontSize: '1.1rem',
                fontWeight: '600',
                color: '#a3bffa',
                display: 'block',
                marginBottom: '0.6rem'
              }}>
                Move History
              </span>
              <ul style={{
                paddingLeft: "6px",
                marginTop: "0.5rem",
                marginBottom: "0px",
                textAlign: "left",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: '0.35rem',
                overflowY: "auto",
                paddingRight: "6px",
                scrollbarWidth: "thin",
                msOverflowStyle: "none",
              }}
                className="custom-scrollbar">
                {history.reduce((acc: string[][], move: { san: string }, index) => {
                  const moveNumber = Math.floor(index / 2) + 1;
                  if (index % 2 === 0) {
                    acc.push([`${moveNumber}. ${move.san}`, ""]);
                  } else {
                    acc[acc.length - 1][1] = move.san;
                  }
                  return acc;
                }, []).map((movePair: string[], index: number) => (
                  <React.Fragment key={index}>
                    <li style={{
                      background: (index * 2 === currentMove - 1) ? 'rgba(99,179,237,0.12)' : 'rgba(255,255,255,0.05)',
                      padding: '0.35rem 0.4rem',
                      borderRadius: '6px',
                      color: '#63b3ed',
                      border: (index * 2 === currentMove - 1) ? '1px solid rgba(99,179,237,0.4)' : '1px solid transparent',
                      cursor: 'pointer'
                    }} onClick={() => goToMoveIndex(index * 2 + 1)}>
                      {movePair[0]}
                    </li>
                    <li style={{
                      background: (index * 2 + 1 === currentMove - 1) ? 'rgba(104,211,145,0.12)' : 'rgba(255,255,255,0.05)',
                      padding: '0.35rem 0.4rem',
                      borderRadius: '6px',
                      color: '#68d391',
                      border: (index * 2 + 1 === currentMove - 1) ? '1px solid rgba(104,211,145,0.4)' : '1px solid transparent',
                      cursor: 'pointer'
                    }} onClick={() => goToMoveIndex(index * 2 + 2)}>
                      {movePair[1]}
                    </li>
                  </React.Fragment>
                ))}
              </ul>
            </div>

            {/* Action Buttons Card */}
            {currentPuzzleData && puzzleScored && (
              <div style={{
                background: 'linear-gradient(135deg, #1a1f25 0%, #2d3748 100%)',
                color: "#fff",
                padding: '0.75rem',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                borderRadius: '10px',
                border: '1px solid rgba(255,255,255,0.1)',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem'
              }}>
                {/* Add to Favorites Button */}
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={toggleFavorite}
                  style={{
                    width: '100%',
                    background: favoritePuzzles.includes(currentPuzzleData.Id)
                      ? 'rgba(229, 62, 62, 0.25)'
                      : 'rgba(229, 62, 62, 0.1)',
                    border: favoritePuzzles.includes(currentPuzzleData.Id)
                      ? '1px solid rgba(229, 62, 62, 0.5)'
                      : '1px solid rgba(229, 62, 62, 0.3)',
                    color: favoritePuzzles.includes(currentPuzzleData.Id)
                      ? '#fc8181'
                      : '#feb2b2',
                    fontSize: '0.85rem',
                    padding: '0.4rem 0.75rem',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    boxShadow: favoritePuzzles.includes(currentPuzzleData.Id)
                      ? '0 0 10px rgba(229, 62, 62, 0.2)'
                      : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (favoritePuzzles.includes(currentPuzzleData.Id)) {
                      e.currentTarget.style.background = 'rgba(229, 62, 62, 0.35)';
                      e.currentTarget.style.borderColor = 'rgba(229, 62, 62, 0.7)';
                      e.currentTarget.style.boxShadow = '0 0 15px rgba(229, 62, 62, 0.3)';
                    } else {
                      e.currentTarget.style.background = 'rgba(229, 62, 62, 0.2)';
                      e.currentTarget.style.borderColor = 'rgba(229, 62, 62, 0.5)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (favoritePuzzles.includes(currentPuzzleData.Id)) {
                      e.currentTarget.style.background = 'rgba(229, 62, 62, 0.25)';
                      e.currentTarget.style.borderColor = 'rgba(229, 62, 62, 0.5)';
                      e.currentTarget.style.boxShadow = '0 0 10px rgba(229, 62, 62, 0.2)';
                    } else {
                      e.currentTarget.style.background = 'rgba(229, 62, 62, 0.1)';
                      e.currentTarget.style.borderColor = 'rgba(229, 62, 62, 0.3)';
                    }
                  }}
                >
                  {favoritePuzzles.includes(currentPuzzleData.Id) ? <FaHeart color="#fc8181" /> : <FaRegHeart color="#feb2b2" />}
                  {favoritePuzzles.includes(currentPuzzleData.Id) ? 'Favorited' : 'Add to Favorites'}
                </Button>

                {/* See Other's Moves Button */}
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={handleShowStats}
                  style={{
                    width: '100%',
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    color: '#a3bffa',
                    fontSize: '0.85rem',
                    padding: '0.4rem 0.75rem',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                    e.currentTarget.style.borderColor = 'rgba(163,191,250,0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                  }}
                >
                  <FaUsers /> See Other's Moves
                </Button>

                {/* Analysis on Lichess Button */}
                {initialPuzzleFen && (
                  <Button
                    variant="outline-light"
                    size="sm"
                    onClick={() => {
                      const url = getLichessAnalysisUrl(initialPuzzleFen);
                      window.open(url, '_blank');
                    }}
                    style={{
                      width: '100%',
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: '#a3bffa',
                      fontSize: '0.85rem',
                      padding: '0.4rem 0.75rem',
                      borderRadius: '6px',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                      e.currentTarget.style.borderColor = 'rgba(163,191,250,0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                    }}
                  >
                    <FaExternalLinkAlt />
                    Analysis on Lichess
                  </Button>
                )}
              </div>
            )}

            {/* Back/Forward Buttons */}
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              width: '100%'
            }}>
              <Button
                className='btn-font-size w-full'
                variant="secondary"
                onClick={handleBackMove}
                disabled={currentMove === 0}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  padding: '0.5rem 1.25rem',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                  fontSize: '0.9rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <FaStepBackward />
              </Button>
              <Button
                className='btn-font-size w-full'
                variant="secondary"
                onClick={() => setOrientation(prev => (prev === 'white' ? 'black' : 'white'))}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  padding: '0.5rem 1.25rem',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                  fontSize: '0.9rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <HiSwitchHorizontal style={{ transform: 'rotate(90deg)' }} />
              </Button>
              <Button
                className='btn-font-size w-full'
                variant="secondary"
                onClick={handleForwardMove}
                disabled={currentMove === history.length}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  padding: '0.5rem 1.25rem',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                  fontSize: '0.9rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <FaStepForward />
              </Button>
            </div>

            {/* Save Button - Hidden in puzzle mode */}
            {
              !currentPuzzleData && (
                <Button
                  className='btn-font-size w-full'
                  variant="success"
                  onClick={handleSaveGame}
                  style={{
                    background: 'linear-gradient(135deg, #34d399 0%, #10b981 100%)',
                    border: 'none',
                    padding: '0.4rem 1rem',
                    borderRadius: '8px',
                    transition: 'all 0.3s ease',
                    fontSize: '0.85rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    fontWeight: '600',
                    boxShadow: 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.filter = 'brightness(1.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.filter = 'brightness(1)';
                  }}
                >
                  <FaSave /> Save game
                </Button>
              )
            }
          </div>
        </div>

        <Modal show={showModal} onHide={handleModalClose} centered>
          <Modal.Header
            closeButton
            style={{
              background: 'linear-gradient(135deg, #1a1f25 0%, #2d3748 100%)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              padding: '1.5rem'
            }}
          >
            <Modal.Title style={{ color: '#fff', fontSize: '1.5rem', fontWeight: '600' }}>
              Save Game
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{
            background: '#1a1f25',
            color: '#fff',
            padding: '2rem',
          }}>
            <Form>
              <Form.Group controlId="formWhitePlayerName" className="mb-4">
                <Form.Label style={{ color: '#a3bffa', marginBottom: '0.5rem' }}>White Player</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter player name"
                  value={whitePlayerName}
                  onChange={(e) => setWhitePlayerName(e.target.value)}
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: '#fff',
                    padding: '0.75rem',
                    borderRadius: '8px'
                  }}
                />
              </Form.Group>
              <Form.Group controlId="formBlackPlayerName">
                <Form.Label style={{ color: '#a3bffa', marginBottom: '0.5rem' }}>Black Player</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter player name"
                  value={blackPlayerName}
                  onChange={(e) => setBlackPlayerName(e.target.value)}
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: '#fff',
                    padding: '0.75rem',
                    borderRadius: '8px'
                  }}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer style={{
            background: 'linear-gradient(135deg, #1a1f25 0%, #2d3748 100%)',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            padding: '1.5rem'
          }}>
            <Button
              variant="secondary"
              onClick={handleModalClose}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                padding: '0.75rem 2rem',
                borderRadius: '8px',
                color: '#fff',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <FaTimes /> Close
            </Button>
            <Button
              variant="primary"
              onClick={handleModalSave}
              style={{
                background: 'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)',
                border: 'none',
                padding: '0.75rem 2rem',
                borderRadius: '8px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <FaCheck /> Save
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showAlertModal} onHide={() => setShowAlertModal(false)} centered>
          <Modal.Header
            closeButton
            style={{
              background: 'linear-gradient(135deg, #1a1f25 0%, #2d3748 100%)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              padding: '1rem 1.5rem'
            }}
          >
            <Modal.Title style={{
              color: '#fff',
              fontSize: '1.5rem',
              fontWeight: '600'
            }}>
              Move Information
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{
            background: '#1a1f25',
            color: '#fff',
            padding: '1rem',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
          }}>
            {moveInfo && (
              <div style={{ fontSize: '1.1rem' }}>
                {/* Removed explicit Move row; user move will be shown in the ranked list */}
                {moveInfo.topMoves && (
                  <div style={{
                    marginBottom: '1rem',
                    padding: '0.5rem 1rem',
                    background: 'rgba(255,255,255,0.04)',
                    borderRadius: '8px'
                  }}>
                    <div style={{ color: '#a3bffa', marginBottom: '0.5rem', textAlign: 'left' }}>Top 5 moves</div>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, textAlign: 'left' }}>
                      {moveInfo.topMoves.map((m) => (
                        <li key={m.move} style={{
                          display: 'flex', justifyContent: 'space-between', padding: '6px 10px',
                          background: (m as any).raw === moveInfo.userMoveKey ? 'rgba(99,179,237,0.12)' : 'transparent',
                          fontWeight: (m as any).raw === moveInfo.userMoveKey ? 700 as any : 500 as any,
                          borderRadius: '6px', marginBottom: '4px',
                          border: (m as any).raw === moveInfo.userMoveKey ? '1px solid rgba(99,179,237,0.6)' : '1px solid transparent',
                          cursor: 'pointer'
                        }} onClick={() => applySuggestedMove((m as any).raw || m.move)}>
                          <span>
                            <span style={{ color: '#a3bffa', marginRight: 6 }}>#{m.rank}</span>
                            {(m as any).raw === moveInfo.userMoveKey && moveInfo.userMoveSan ? moveInfo.userMoveSan : m.move}
                          </span>
                          <span style={{ color: '#e2e8f0' }}>{formatMoveScore(m.score)}</span>
                        </li>
                      ))}
                      {moveInfo.userRank && moveInfo.userRank > 5 && (
                        <li style={{
                          display: 'flex', justifyContent: 'space-between', padding: '6px 10px',
                          background: 'rgba(99,179,237,0.12)', fontWeight: 700 as any,
                          borderRadius: '6px', border: '1px solid rgba(99,179,237,0.6)',
                          cursor: 'pointer'
                        }} onClick={() => applySuggestedMove(moveInfo.userMoveKey!)}>
                          <span>
                            <span style={{ color: '#a3bffa', marginRight: 6 }}>#{moveInfo.userRank}</span>
                            {moveInfo.userMoveSan || moveInfo.userMoveKey}
                          </span>
                          <span style={{ color: '#e2e8f0' }}>{formatMoveScore(moveInfo.score ?? 0)}</span>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
                <p style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '1rem',
                  padding: '0.5rem 1.5rem',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '8px'
                }}>
                  <strong>Score:</strong>
                  <span style={{ color: '#68d391' }}>{moveInfo.score !== undefined ? formatMoveScore(moveInfo.score) : 'Score not found'}</span>
                </p>
                <p style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '1rem',
                  padding: '0.5rem 1.5rem',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '8px'
                }}>
                  <strong>Elapsed Time:</strong>
                  <span style={{ color: '#f6ad55' }}>{moveInfo.time}</span>
                </p>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer style={{
            background: 'linear-gradient(135deg, #1a1f25 0%, #2d3748 100%)',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            padding: '0.5rem'
          }}>
            <Button
              variant="success"
              onClick={handleNext}
              disabled={nextButtonDisabled || isLoadingNext}
              style={{
                background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                border: 'none',
                opacity: (nextButtonDisabled || isLoadingNext) ? 0.5 : 1,
                cursor: (nextButtonDisabled || isLoadingNext) ? 'not-allowed' : 'pointer',
                padding: '0.75rem 2rem',
                borderRadius: '8px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <FaArrowRight /> Next
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showAlertModal} onHide={() => setShowAlertModal(false)} centered>
          <Modal.Header
            closeButton
            style={{
              background: 'linear-gradient(135deg, #1a1f25 0%, #2d3748 100%)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              padding: '1.5rem'
            }}
          >
            <Modal.Title style={{
              color: '#fff',
              fontSize: '1.5rem',
              fontWeight: '600'
            }}>
              Information
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{
            background: '#1a1f25',
            color: '#fff',
            padding: '2rem',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)',
            textAlign: 'center',
            fontSize: '1.1rem'
          }}>
            {alertMessage}
          </Modal.Body>
          <Modal.Footer style={{
            background: 'linear-gradient(135deg, #1a1f25 0%, #2d3748 100%)',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            padding: '1.5rem',
            justifyContent: 'center'
          }}>
            <Button
              variant="primary"
              onClick={() => {
                setShowAlertModal(false);
                // If message is about no puzzles, navigate to home
                if (alertMessage === 'No new puzzles available') {
                  navigate('/');
                }
              }}
              style={{
                background: 'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)',
                border: 'none',
                padding: '0.75rem 2rem',
                borderRadius: '8px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <FaCheck /> OK
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Move Stats Modal */}
        <Modal show={showStatsModal} onHide={() => setShowStatsModal(false)} centered>
          <Modal.Header
            closeButton
            style={{
              background: 'linear-gradient(135deg, #1a1f25 0%, #2d3748 100%)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              padding: '1rem 1.5rem'
            }}
          >
            <Modal.Title style={{
              color: '#fff',
              fontSize: '1.3rem',
              fontWeight: '600'
            }}>
              Other Users' Moves
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{
            background: '#1a1f25',
            color: '#fff',
            padding: '1.5rem',
            maxHeight: '60vh',
            overflowY: 'auto'
          }}>
            {loadingStats ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="light" />
                <p className="mt-2 text-gray-400">Loading stats...</p>
              </div>
            ) : (
              <>
                {!puzzleMoveStats || Object.keys(puzzleMoveStats).length === 0 ? (
                  <div className="text-center text-gray-400">
                    No move data available for this puzzle yet.
                  </div>
                ) : (
                  <div>
                    <p className="text-gray-400 mb-4 text-sm">
                      Moves played by other users in this position:
                    </p>
                    <div className="space-y-2">
                      {Object.entries(puzzleMoveStats)
                        .sort(([, countA], [, countB]) => countB - countA)
                        .map(([move, count], index) => {
                          const total = Object.values(puzzleMoveStats).reduce((a, b) => a + b, 0);
                          const percentage = Math.round((count / total) * 100);

                          return (
                            <div key={move} style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '0.75rem',
                              background: index === 0 ? 'rgba(72, 187, 120, 0.1)' : 'rgba(255,255,255,0.05)',
                              border: index === 0 ? '1px solid rgba(72, 187, 120, 0.3)' : '1px solid rgba(255,255,255,0.1)',
                              borderRadius: '8px',
                              marginBottom: '0.5rem'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{
                                  fontWeight: 'bold',
                                  color: index === 0 ? '#68d391' : '#e2e8f0',
                                  fontSize: '1.1rem'
                                }}>
                                  {move}
                                </span>
                                {index === 0 && (
                                  <span style={{
                                    fontSize: '0.7rem',
                                    background: '#48bb78',
                                    color: '#fff',
                                    padding: '2px 6px',
                                    borderRadius: '4px'
                                  }}>
                                    Most Played
                                  </span>
                                )}
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontWeight: 'bold', color: '#fff' }}>{percentage}%</div>
                                <div style={{ fontSize: '0.8rem', color: '#a0aec0' }}>{count} plays</div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer style={{
            background: 'linear-gradient(135deg, #1a1f25 0%, #2d3748 100%)',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            padding: '0.75rem'
          }}>
            <Button variant="secondary" onClick={() => setShowStatsModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
        {/* Date Selection Modal */}
        <Modal show={showDateSelectionModal} onHide={() => setShowDateSelectionModal(false)} centered className="dark-modal">
          <Modal.Header closeButton style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', background: '#1a1f25', color: '#fff' }}>
            <Modal.Title>Select Puzzle Date</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ background: '#1a1f25', color: '#fff' }}>
            <p style={{ color: '#a3bffa', marginBottom: '1rem' }}>
              Choose a date to solve puzzles from.
            </p>
            <ListGroup variant="flush">
              {availableDates.map((date, index) => {
                const count = allPuzzles.filter(p => {
                  if (!(p as any).createdAt) return false;
                  return new Date((p as any).createdAt).toLocaleDateString('tr-TR') === date;
                }).length;

                return (
                  <ListGroup.Item
                    key={index}
                    action
                    onClick={() => handleDateSelect(date)}
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      color: '#fff',
                      marginBottom: '0.5rem',
                      borderRadius: '8px',
                      border: '1px solid rgba(255,255,255,0.1)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                    className="date-selection-item"
                  >
                    <span>{date}</span>
                    <span style={{
                      background: 'rgba(99,179,237,0.2)',
                      color: '#90cdf4',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '0.85rem'
                    }}>
                      {count} puzzles
                    </span>
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
}

export default App;
